// Generative artifacts — agent-drafted documents from structured data.
// Stand-ins for model generation; in Phase 2 these become prompt templates.

import { fullName, money } from './data';
import { SOURCES } from './networkStore';
import { MOCK_CLAIMS, MOCK_DENIAL_PATTERNS } from '../mock';

const today = 'May 4, 2026';

const ARG_BY_CARC = {
  '197': 'The eligibility verification on file (271) returned no indication that prior authorization was required for this service on the date of service. We therefore request reconsideration and reprocessing of this claim.',
  '16': 'The information cited as missing is enclosed with this appeal (corrected demographics and diagnosis codes verified against the payer 271). Please reprocess.',
  '96': 'This service was medically necessary as documented in the enclosed clinical notes. We request the non-covered determination be overturned.',
  '50': 'A signed Advance Beneficiary Notice (ABN) and supporting clinical documentation establishing medical necessity are enclosed. We request reconsideration.',
};

export function appealLetter(claim, patient, payer) {
  const code = (claim.denialCode || '').replace(/\D/g, '');
  const arg = ARG_BY_CARC[code] || 'We believe this claim was denied in error and request a full reconsideration based on the enclosed documentation.';
  return `${today}

RE: Claim Appeal — ${claim.id}
Patient: ${fullName(patient)}  ·  DOB: ${patient?.dob}  ·  Member ID: ••••${patient?.memberIdLast4}
Payer: ${payer?.name}
Date of service: ${claim.dos}  ·  CPT: ${claim.cpt}  ·  Billed: ${money(claim.totalCharge)}
Denial: ${claim.denialCode} — ${claim.denialReason}

To the Appeals Department,

We are formally appealing the denial of the above claim. ${arg}

Per our records, eligibility was verified prior to the date of service and the patient's coverage was active. We respectfully request that this claim be reprocessed for payment within the timeframe required by the plan.

Enclosures: eligibility response (271), itemized claim, and supporting clinical documentation.

Sincerely,
Billing Department
Pacific Dermatology Group  ·  NPI 1942758109`;
}

// Denial digest — a reviewed periodic summary of valid denials for RCM.
export function denialDigest(practiceId = 'prac_001', period = 'This period') {
  const denied = MOCK_CLAIMS.filter((c) => c.practiceId === practiceId && c.status === 'denied');
  const total = denied.reduce((a, c) => a + c.totalCharge, 0);
  const byReason = {};
  denied.forEach((c) => { const k = c.denialCode || 'Other'; (byReason[k] ||= { count: 0, amt: 0, reason: c.denialReason }); byReason[k].count += 1; byReason[k].amt += c.totalCharge; });
  const reasonLines = Object.entries(byReason)
    .sort((a, b) => b[1].amt - a[1].amt)
    .map(([code, v]) => `- ${code} (${v.reason}): ${v.count} denial${v.count === 1 ? '' : 's'}, ${money(v.amt)}`)
    .join('\n');
  const actions = MOCK_DENIAL_PATTERNS.slice(0, 3).map((d) => `- ${d.planName}: ${d.recommendedAction}`).join('\n');

  return `DENIAL DIGEST — ${period}
Generated May 4, 2026  ·  Pacific Dermatology Group

SUMMARY
${denied.length} valid denials totaling ${money(total)}. These are surfaced as learning signals; V1 focuses on paid circumstances, so this digest is a reviewed manual summary rather than an auto-mapped feed.

BY DENIAL REASON
${reasonLines || '- None this period'}

RECOMMENDED POLICY ACTIONS
${actions}

NEXT STEP
Review with RCM, then convert the top recurring reason into an enforced policy so the front desk prevents it at eligibility time.`;
}

// Appeal evidence (gap 2.7) — packages the network-verification audit trail into
// proof for an appeal when a payer denies despite confirming network status. Pulls
// every logged call (reference #, rep, date, staff) so the denial can be contested
// with the payer's own representations. The system never takes the fall.
export function appealEvidencePacket({ plan, payerName, verifications, confidence, practiceName: pName = 'Pacific Dermatology Group' }) {
  const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—');
  const evs = [...(verifications || [])].sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0));
  const rows = evs.length
    ? evs.map((v, i) => `${i + 1}. ${fmt(v.date)} — ${v.result === 'in_network' ? 'IN-NETWORK confirmed' : 'Out-of-network'}
     Reference #: ${v.referenceNumber || '—'}   ·   Payer rep: ${v.repName || '—'}
     Verified by: ${v.staffName || '—'}   ·   Source: ${SOURCES[v.source] || v.source || '—'}`).join('\n\n')
    : '   (No verification calls on record.)';
  const conf = confidence || { count: evs.length, threshold: 10, level: 'low' };
  const statusLabel = plan?.status === 'in_network' ? 'IN-NETWORK' : plan?.status === 'out_of_network' ? 'OUT-OF-NETWORK' : (plan?.status || '—');

  return `NETWORK VERIFICATION — APPEAL EVIDENCE PACKET
Generated ${today}  ·  ${pName}

PLAN
${plan?.planName || '—'}  ·  Payer: ${payerName || '—'}
Network status on file: ${statusLabel}
Verification confidence: ${conf.count} of ${conf.threshold} confirmations (${conf.level === 'high' ? 'HIGH — trusted' : 'building'})
First added to network library: ${fmt(plan?.addedAt)} by ${plan?.addedBy || '—'} (${SOURCES[plan?.source] || plan?.source || '—'})

GROUNDS FOR APPEAL
The practice verified this plan's network participation directly with the payer on the call(s) documented below. Each confirmation is logged with the payer representative's name, the call reference number, the date, and the staff member who placed the call. This denial contradicts the payer's own repeated representations of network status.

VERIFICATION HISTORY
${rows}

REQUEST
We request that this claim be reprocessed at the in-network benefit level consistent with the payer's confirmations above. Should the payer maintain the denial, we ask for a written explanation that reconciles it with the referenced calls.

${pName}  ·  NPI 1942758109`;
}

export function priorAuthPacket(item, patient, payer) {
  return `PRIOR AUTHORIZATION REQUEST
Generated ${today}

Patient: ${fullName(patient)}  ·  DOB: ${patient?.dob}  ·  Member ID: ••••${patient?.memberIdLast4}
Payer: ${payer?.name}  ·  Plan: ${item.title}
Requested service: CPT ${item.cpt || '—'}
Rendering provider: Dr. Martinez  ·  NPI 1942758109

Clinical justification:
${item.agentSummary}

Checklist:
[x] Eligibility verified (271 on file)
[x] Diagnosis codes captured at intake
[ ] Clinical notes attached
[ ] Prior conservative treatment documented (if required)

Submit to: ${payer?.name} Utilization Management
Track auth number to claim before date of service.`;
}
